import { useMemo } from 'react';
import PropTypes from 'prop-types';

import LoadingButton from '@mui/lab/LoadingButton';

import { usePlan } from 'src/hooks/use-plan';

import Iconify from '../iconify';
import LimitButton from '../limit-button';
import { creditUpgrade } from './credit-upgrade';

// ----------------------------------------------------------------------

export default function AIGenerateButton ({ onClick, loading, disabled, size = 'small', ...other }) {
  const { usage, hasAICredits } = usePlan();

  const isAtLimit = !hasAICredits;
  const currentPlan = usage?.plan || 'starter';
  const usedCredits = usage ? usage.limits.aiCreditsPerMonth - usage.usage.aiCredits : 0;
  const totalCredits = usage?.limits.aiCreditsPerMonth || 10;
  const upgrade = useMemo(() => creditUpgrade(currentPlan, totalCredits), [currentPlan, totalCredits]);

  // Memoize popover content to avoid recreating on every render
  const popoverContent = useMemo(() => {
    if (!upgrade) {
      return {
        title: "You've used all your AI credits",
        description: (
          <>
            You&apos;ve used all <strong>{totalCredits.toLocaleString('en-US')} AI credits</strong> this month. They
            renew next month.
          </>
        ),
        benefits: [],
      };
    }

    const benefits = [
      <>
        <strong>{upgrade.credits}</strong> AI credits a month
      </>,
      'Your credits renew every month',
    ];

    // Add plan-specific benefits for Starter users
    if (currentPlan === 'starter') {
      benefits.push('Sales detection & auto-delist', 'Bulk cross-listing');
    }

    return {
      title: "You've used all your AI credits",
      description: (
        <>
          You&apos;ve used all <strong>{totalCredits.toLocaleString('en-US')} AI credits</strong> this month. Upgrade to{' '}
          <strong>{upgrade.name}</strong> for <strong>{upgrade.credits} credits a month</strong>.
        </>
      ),
      benefits,
    };
  }, [currentPlan, upgrade, totalCredits]);

  return (
    <LimitButton
      isAtLimit={isAtLimit}
      limitLabel={`AI Credits Used (${usedCredits}/${totalCredits})`}
      popoverTitle={popoverContent.title}
      popoverDescription={popoverContent.description}
      popoverBenefits={popoverContent.benefits}
      popoverIcon="solar:star-bold"
    >
      <LoadingButton
        variant="contained"
        size={size}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        startIcon={<Iconify icon="streamline:ai-generate-variation-spark" />}
        {...other}
      >
        Generate Listing with AI
      </LoadingButton>
    </LimitButton>
  );
}

AIGenerateButton.propTypes = {
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.string,
};
